import type { ElementType, KeyboardEvent, ReactNode } from 'react'
import type { PolymorphicProps, PolymorphicRef } from './polymorphic'

interface CardOwnProps {
  children?: ReactNode
  className?: string
  bodyClassName?: string
  title?: string
  subtitle?: string
  description?: string
  actions?: ReactNode
  footer?: ReactNode
  compact?: boolean
  onClick?: () => void
  /**
   * Skip the injected `card-body` wrapper (and title/subtitle/description/
   * actions/footer handling, which only make sense with a body) and render
   * `children` directly inside the `card` root. For bare containers that
   * own their own padding/layout (a full-bleed `<ul>`, a caller-padded tile).
   */
  noBody?: boolean
}

export type CardProps<C extends ElementType = 'div'> = PolymorphicProps<C, CardOwnProps>

export function Card<C extends ElementType = 'div'>(props: CardProps<C>) {
  // Same narrowing workaround as Button (see polymorphic.ts docs): TS can't
  // prove a default like `className = ''` is assignable for an arbitrary `C`.
  const {
    as,
    className = '',
    bodyClassName = '',
    title,
    subtitle,
    description,
    actions,
    footer,
    compact,
    onClick,
    noBody,
    children,
    ref,
    ...rest
  } = props as CardOwnProps & {
    as?: ElementType
    ref?: PolymorphicRef<C>
  } & Record<string, unknown>

  const Component = (as ?? 'div') as ElementType
  const isDefaultDiv = Component === 'div'
  const interactive = Boolean(onClick) || !isDefaultDiv
  // Keyboard activation is only wired up for the plain onClick-div case —
  // Link/'a'/'button' already carry native focus + Enter/Space semantics, so
  // adding role/tabIndex/onKeyDown there would double-handle the interaction.
  const needsKeyboardActivation = Boolean(onClick) && isDefaultDiv

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (event.key === ' ') event.preventDefault()
      onClick?.()
    }
  }

  // Suppress default shadow when the caller passes a shadow-* override or bare `shadow`.
  // The character class allows internal hyphens (`[a-z0-9-]+`, not `[a-z0-9]+`) so
  // multi-segment custom tokens like `shadow-fd-xs` (EP-114) are recognized as an
  // override too, not just single-segment ones like `shadow-xl`/`shadow-sm`.
  const hasShadow = /(^|\s)shadow(-[a-z0-9-]+)?(\s|$)/.test(className)
  // Suppress default bg-base-100 when the caller supplies any bg-* class
  const hasBg = /(^|\s)bg-/.test(className)
  const interactiveClasses = interactive ? 'hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow' : ''
  const wrapper = `card ${hasBg ? '' : 'bg-base-100'} ${hasShadow ? '' : 'shadow'} ${interactiveClasses} ${className}`.replace(/\s+/g, ' ').trim()
  const body = `card-body ${compact ? 'p-4' : ''} ${bodyClassName}`.replace(/\s+/g, ' ').trim()

  if (noBody) {
    return (
      <Component
        ref={ref as PolymorphicRef<C>}
        className={wrapper}
        onClick={onClick}
        role={needsKeyboardActivation ? 'button' : undefined}
        tabIndex={needsKeyboardActivation ? 0 : undefined}
        onKeyDown={needsKeyboardActivation ? handleKeyDown : undefined}
        {...rest}
      >
        {children}
      </Component>
    )
  }

  return (
    <Component
      ref={ref as PolymorphicRef<C>}
      className={wrapper}
      onClick={onClick}
      role={needsKeyboardActivation ? 'button' : undefined}
      tabIndex={needsKeyboardActivation ? 0 : undefined}
      onKeyDown={needsKeyboardActivation ? handleKeyDown : undefined}
      {...rest}
    >
      <div className={body}>
        {(title || actions) && (
          <div className="flex items-center justify-between mb-2">
            <div>
              {title && <h2 className="card-title">{title}</h2>}
              {(subtitle || description) && <p className="text-base-content/60 text-sm">{subtitle || description}</p>}
            </div>
            {actions && <div className="card-actions">{actions}</div>}
          </div>
        )}
        {children}
        {footer && <div className="card-actions justify-end mt-4">{footer}</div>}
      </div>
    </Component>
  )
}

Card.displayName = 'Card'

export interface StatCardProps {
  title: string
  value: string | number
  icon?: ReactNode
  description?: string
  trend?: {
    value: number
    isPositive: boolean
  }
  className?: string
}

export function StatCard({ title, value, icon, description, trend, className = '' }: StatCardProps) {
  return (
    <div className={`card bg-base-100 shadow ${className}`}>
      <div className="card-body">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base-content/60 text-sm">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
            {description && <p className="text-base-content/60 text-sm mt-1">{description}</p>}
            {trend && (
              <p className={`text-sm mt-1 ${trend.isPositive ? 'text-success' : 'text-error'}`}>
                {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
              </p>
            )}
          </div>
          {icon && (
            <div className="text-primary opacity-80">
              {icon}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
